import { MdPlayArrow } from 'react-icons/md';
import { useLanguage } from '@/i18n/LanguageContext';
import ScrollReveal from './ScrollReveal';

export default function Glimpse() {
    const { t } = useLanguage();
    return (
        <section className="px-4 py-16 lg:px-16 lg:py-24 xl:px-20">
            {/* Section Header */}
            <div className="pb-6 pt-10 2xl:pb-12 2xl:pt-24">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:gap-16">
                    <ScrollReveal variant="fade-up" delay={0}>
                        <div>
                            <h2 className="text-secondary max-w-4xl text-[28px] font-extrabold leading-[1.1] tracking-tight sm:text-[36px] lg:text-[44px] xl:text-[56px] 2xl:text-[64px]">
                                {t('glimpse.heading')}
                            </h2>
                        </div>
                    </ScrollReveal>
                    <ScrollReveal variant="fade-up" delay={150}>
                        <div>
                            <p className="text-[#4E4D4D] max-w-3xl text-sm font-medium leading-[2] lg:text-[15px]">
                                {t('glimpse.subtitle')}
                            </p>
                        </div>
                    </ScrollReveal>
                </div>
            </div>

            {/* Banner Card */}
            <ScrollReveal variant="fade-up" delay={100} distance={36} duration={800}>
                <div className="flex flex-col overflow-hidden rounded-3xl lg:h-[420px] lg:flex-row xl:h-[460px]">
                    {/* Left — Stats Card (Dark with background image) */}
                    <div className="bg-secondary relative flex min-h-[280px] flex-col justify-between overflow-hidden p-8 lg:min-h-0 lg:w-[38%] lg:p-10 xl:w-[36%] xl:p-12">
                        {/* Background image */}
                        <img
                            src="/tem/bg-60.png"
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-lighten"
                        />

                        {/* Stats Content */}
                        <div className="relative z-10">
                            <h3 className="text-[48px] font-extrabold leading-none tracking-tight sm:text-[64px] lg:text-[80px] xl:text-[88px]">
                                <span className="text-white">{t('glimpse.stats')}</span>
                                <span className="text-primary">+</span>
                            </h3>
                            <p className="mt-2 text-base text-white/50 lg:text-lg">{t('glimpse.statsLabel')}</p>
                        </div>

                        {/* Avatar Stack */}
                        <div className="relative z-10 mt-8 flex items-center lg:mt-0">
                            {[1, 2, 3, 4].map((n, i) => (
                                <img
                                    key={n}
                                    src={`/tem/avatar${n}.png`}
                                    alt={`User avatar ${n}`}
                                    className={`border-secondary h-12 w-12 rounded-full border-2 bg-black object-cover lg:h-14 lg:w-14 ${
                                        i > 0 ? '-ml-3 lg:-ml-4' : ''
                                    }`}
                                />
                            ))}
                            <span className="ml-3 text-2xl font-light text-white lg:text-3xl">+</span>
                        </div>
                    </div>

                    {/* Right — Image + "How it works" */}
                    <div className="relative min-h-[260px] flex-1 lg:min-h-0">
                        <img
                            src="/assets/libs/how-it-works.png"
                            alt="Team collaborating on legal solutions"
                            className="absolute h-full w-full object-cover md:relative"
                        />

                        {/* Dark gradient overlay at bottom */}
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                        {/* "How it works" overlay */}
                        <div className="absolute inset-x-0 bottom-6 flex flex-col items-center gap-3 md:inset-x-auto md:left-6 md:flex-row md:items-end md:gap-4 lg:bottom-8 lg:left-auto lg:right-10 lg:items-center xl:right-12">
                            <p className="mr-[35px] text-[26px] font-bold leading-none tracking-tight text-white sm:text-[34px] lg:text-[48px] xl:text-[56px]">
                                {t('glimpse.howItWorks')}
                            </p>
                            <div className="rounded-full bg-white p-[10px] md:absolute md:bottom-[-37px] md:right-[-53px]">
                                <button
                                    type="button"
                                    className="bg-primary hover:bg-primary-400 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 hover:scale-105 active:scale-95 lg:h-16 lg:w-16"
                                    aria-label="Play video — How it works"
                                >
                                    <MdPlayArrow className="ml-0.5 h-7 w-7 text-white lg:h-9 lg:w-9" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollReveal>
        </section>
    );
}
